import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Star, Heart, User } from "lucide-react";

export const RatingDialog = ({
  open,
  onOpenChange,
  rating,
  setRating,
  review,
  setReview,
  onSubmit,
  submitting,
  // New props for favorite rider feature
  driverName,
  isRiderFavorite,
  onAddToFavorites,
  addingToFavorites,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-[95vw]">
        <DialogHeader>
          <DialogTitle>Rate Your Ride</DialogTitle>
        </DialogHeader>

        <div className="py-6 space-y-6">
          {/* Star Rating */}
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                className="p-1 transition-transform hover:scale-110"
                data-testid={`rating-star-${star}`}
              >
                <Star
                  className={`w-10 h-10 ${
                    star <= rating
                      ? "fill-yellow-400 text-yellow-400"
                      : "text-gray-300"
                  }`}
                />
              </button>
            ))}
          </div>

          <p className="text-center text-sm text-muted-foreground">
            {rating === 1 && "Poor"}
            {rating === 2 && "Fair"}
            {rating === 3 && "Good"}
            {rating === 4 && "Very Good"}
            {rating === 5 && "Excellent!"}
          </p>

          {/* Review Text */}
          <Textarea
            placeholder="Share your experience (optional)"
            value={review}
            onChange={(e) => setReview(e.target.value)}
            className="resize-none"
            rows={3}
            data-testid="rating-review-input"
          />

          {/* Add to Favorites Option */}
          {onAddToFavorites && !isRiderFavorite && rating >= 4 && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-red-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">
                    Add {driverName || "this rider"} to favorites?
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Request them for future rides
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 border-red-300 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30"
                  onClick={onAddToFavorites}
                  disabled={addingToFavorites}
                  data-testid="add-to-favorites-btn"
                >
                  <Heart className={`w-4 h-4 ${addingToFavorites ? "animate-pulse" : ""}`} />
                  {addingToFavorites ? "Adding..." : "Add"}
                </Button>
              </div>
            </div>
          )}

          {/* Already a favorite indicator */}
          {isRiderFavorite && (
            <div className="flex items-center justify-center gap-2 text-sm text-red-500">
              <Heart className="w-4 h-4 fill-red-500" />
              <span>{driverName || "This rider"} is in your favorites</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            className="w-full"
            onClick={onSubmit}
            disabled={submitting}
            data-testid="submit-rating-btn"
          >
            {submitting ? "Submitting..." : "Submit Rating"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RatingDialog;
